/**
 * AvatarPicker — lets students choose their hero's appearance.
 * Skin tone, hair / cap colour, and robe colour.
 * Preview SVG mirrors the exact pixels drawn in Boot.ts / Overworld.ts.
 */
import { useState } from 'react'
import type { AvatarConfig } from '@/context/GameContext'

// ── Colour palettes ───────────────────────────────────────────────────────────
// Skin tones: six shades from very fair to deep
const SKIN_OPTIONS = [0xfde8c8, 0xf5c5a0, 0xe8a87c, 0xc98c5a, 0x8d5524, 0x4a2511]
// Hair colours: black → dark brown → auburn → blonde → light blonde → fun purple
const HAIR_OPTIONS = [0x1a1a1a, 0x3b2314, 0x7b4f2e, 0xc19a50, 0xe8d090, 0x7c3aed]
// Outfit / robe colours
const OUTFIT_OPTIONS = [0x4338ca, 0x064e3b, 0x7f1d1d, 0x0e7490, 0xb45309]

// Phaser hex  →  CSS hex string
const toCss = (n: number) => `#${n.toString(16).padStart(6, '0')}`

// ── Swatch row component ──────────────────────────────────────────────────────
function Swatches({
  label, options, value, onChange,
}: {
  label: string
  options: number[]
  value: number
  onChange: (n: number) => void
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        fontFamily: '"Press Start 2P", monospace', fontSize: '6px',
        color: '#818cf8', marginBottom: 8, letterSpacing: 1,
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        {options.map(n => (
          <button
            key={n}
            onClick={() => onChange(n)}
            style={{
              width: 30, height: 30,
              background: toCss(n),
              border: n === value ? '3px solid #fbbf24' : '2px solid #312e81',
              borderRadius: 4,
              cursor: 'pointer',
              boxShadow: n === value ? '0 0 10px #fbbf24' : 'none',
              transition: 'box-shadow 0.15s, border 0.15s',
              padding: 0,
            }}
            aria-label={toCss(n)}
          />
        ))}
      </div>
    </div>
  )
}

// ── Character preview — matches BackpackKid in SuperCivicaWorld ──────────────
function HeroPreview({ skin, hair, outfit }: { skin: number; hair: number; outfit: number }) {
  const s = toCss(skin), h = toCss(hair), o = toCss(outfit)
  return (
    <svg
      viewBox="-16 -20 32 42" width="150" height="200"
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="crispEdges"
      style={{ filter: 'drop-shadow(0 0 14px #a78bfa)', display: 'block' }}
    >
      <ellipse cx="0" cy="17" rx="8" ry="2" fill="#000" opacity=".25"/>
      <rect x="-7" y="-15" width="14" height="5" fill={h}/>
      <rect x="-8" y="-13" width="2"  height="3" fill={h}/>
      <rect x="6"  y="-13" width="2"  height="3" fill={h}/>
      <rect x="-6" y="-10" width="12" height="9" fill={s}/>
      <rect x="-4" y="-8" width="2" height="2" fill="#1a0a00"/>
      <rect x="2"  y="-8" width="2" height="2" fill="#1a0a00"/>
      <rect x="-2" y="-2" width="4" height="1" fill="#1a0a00" opacity=".55"/>
      <rect x="-7" y="-1" width="14" height="14" fill={o}/>
      <rect x="-7" y="-1" width="14" height="2"  fill={s} opacity=".45"/>
      <rect x="5" y="1" width="6" height="9" fill="#5a3a1a"/>
      <rect x="5" y="1" width="6" height="2" fill="#c08a2a"/>
      <rect x="-10" y="1" width="3" height="6" fill={s}/>
      <rect x="-14" y="0" width="6" height="5" fill="#efe4c4" stroke="#7a6232" strokeWidth=".6"/>
      <rect x="7" y="1" width="3" height="6" fill={s}/>
      <rect x="-6" y="13" width="4" height="3" fill={o}/>
      <rect x="2"  y="13" width="4" height="3" fill={o}/>
      <rect x="-7" y="16" width="5" height="2" fill="#1a0a00"/>
      <rect x="2"  y="16" width="5" height="2" fill="#1a0a00"/>
    </svg>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
interface Props {
  initial: AvatarConfig
  onSave: (a: AvatarConfig) => void
  /** Optional label — "SAVE HERO" on first setup, "UPDATE HERO" from menu */
  saveLabel?: string
}

export default function AvatarPicker({ initial, onSave, saveLabel = 'SAVE HERO' }: Props) {
  const [skin,   setSkin]   = useState(initial.skin)
  const [hair,   setHair]   = useState(initial.hair)
  const [outfit, setOutfit] = useState(initial.outfit)

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 50,
      background: 'rgba(0,0,0,0.88)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#0f0f23',
        border: '3px solid #6366f1',
        borderRadius: 14,
        padding: '28px 30px',
        maxWidth: 400,
        width: '90%',
        textAlign: 'center',
        boxShadow: '0 0 40px rgba(99,102,241,0.3)',
      }}>
        {/* Title */}
        <div style={{
          fontFamily: '"Press Start 2P", monospace', fontSize: '9px',
          color: '#a78bfa', marginBottom: 20, lineHeight: 1.6,
        }}>
          ⚔ CUSTOMIZE YOUR HERO
        </div>

        {/* Live preview */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <HeroPreview skin={skin} hair={hair} outfit={outfit} />
        </div>

        {/* Swatches */}
        <Swatches label="SKIN TONE"  options={SKIN_OPTIONS}   value={skin}   onChange={setSkin}   />
        <Swatches label="HAIR COLOR" options={HAIR_OPTIONS}   value={hair}   onChange={setHair}   />
        <Swatches label="OUTFIT"     options={OUTFIT_OPTIONS} value={outfit} onChange={setOutfit} />

        {/* Save button */}
        <button
          onClick={() => onSave({ skin, hair, outfit })}
          style={{
            marginTop: 10,
            fontFamily: '"Press Start 2P", monospace', fontSize: '8px',
            color: '#fff',
            background: '#4f46e5',
            border: '2px solid #818cf8',
            borderRadius: 6,
            padding: '12px 0',
            cursor: 'pointer',
            width: '100%',
            letterSpacing: 1,
          }}
        >
          ▶ {saveLabel}
        </button>
      </div>
    </div>
  )
}
